/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { Button as ButtonCore, type ButtonProps as ButtonCoreProps, type ButtonRenderProps, type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { mergeStyles } from '../../utils/merge-styles/merge-styles.util';
import { resolveComponentStyle } from '../../utils/resolve-component-style/resolve-component-style.util';
import { IconContext } from '../icon/icon.component';
import { Text, TextContext, type TextProps } from '../text/text.component';

interface TextButtonProps extends Omit<ButtonCoreProps, 'style' | 'className' | 'isPending'> {
  /** Wether the button has safe area. (Default: none) */
  safeArea?: 'rounded' | 'square' | 'none';
  /** The variant of the button. (Default: primary) */
  variant?: keyof typeof safeAreaVariants;
  /** The size of the button. (Default: 32) */
  size?: keyof typeof sizeVariants;
  style?: StyleOrFunction<ButtonRenderProps> | {
    base?: StyleOrFunction<ButtonRenderProps>;
    icon?: StyleOrFunction<ButtonRenderProps>;
    text?: StyleOrFunction<ButtonRenderProps>;
  };
}

const TextButtonContext = createContext<ContextValue<TextButtonProps, HTMLButtonElement>>({});

const TextButton = forwardRef(({ style: styleProp, ...props }: TextButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, TextButtonContext);

  const { children, variant = 'primary', size = 32, safeArea = 'none', style: contextStyle, ...coreProps } = props as TextButtonProps;

  return (
    <ButtonCore
      ref={ref}
      {...applyStylesWithRenderProps<ButtonRenderProps>((state) => stylex.props(
        styles.base,
        sizeVariants[size],
        safeArea !== 'none' && safeAreaSizeVariants[size],
        safeArea !== 'none' && safeAreaVariants[variant],
        safeArea === 'rounded' && safeAreaStyles.rounded,
        mergeStyles(resolveComponentStyle(contextStyle, state, 'base'), resolveComponentStyle(styleProp, state, 'base')),
      ))}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        const contentColor = (
          renderProps.isDisabled && 'disabled' ||
          variant === 'primary' && 'interactive' ||
          variant === 'secondary' && 'primary' ||
          variant === 'tertiary' && renderProps.isHovered && 'primary' ||
          variant === 'tertiary' && 'tertiary' ||
          'secondary'
        );

        return (
          <Provider
            values={[
              [TextContext, {
                style: [textStyles.base, ...mergeStyles(resolveComponentStyle(contextStyle, renderProps, 'text'), resolveComponentStyle(styleProp, renderProps, 'text'))],
                size: TEXT_SIZES[size],
                color: contentColor,
                slot: null!,
              }],
              [IconContext, {
                slots: {
                  [TEXT_BUTTON_SLOTS.LEFT_ICON]: {
                    style: [iconStyles.base, ...mergeStyles(resolveComponentStyle(contextStyle, renderProps, 'icon'), resolveComponentStyle(styleProp, renderProps, 'icon'))],
                    size: ICON_LEFT_SIZES[size],
                    color: contentColor,
                  },
                  [TEXT_BUTTON_SLOTS.RIGHT_ICON]: {
                    style: [iconStyles.base, ...mergeStyles(resolveComponentStyle(contextStyle, renderProps, 'icon'), resolveComponentStyle(styleProp, renderProps, 'icon'))],
                    size: ICON_RIGHT_SIZES[size],
                    color: contentColor,
                  },
                }
              }],
            ]}>
            {typeof evaluatedChildren === 'string' || isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
          </Provider>
        );
      }}
    </ButtonCore>
  );
});

const TEXT_SIZES: Record<keyof typeof safeAreaSizeVariants, TextProps['size']> = {
  28: 12,
  32: 13,
  40: 14,
  48: 16,
  52: 16,
  64: 20,
};

const ICON_LEFT_SIZES: Record<keyof typeof safeAreaSizeVariants, number> = {
  28: 12,
  32: 16,
  40: 16,
  48: 20,
  52: 20,
  64: 24,
};

const ICON_RIGHT_SIZES: Record<keyof typeof safeAreaSizeVariants, number> = {
  28: 12,
  32: 12,
  40: 12,
  48: 16,
  52: 16,
  64: 16,
};

const styles = stylex.create({
  base: {
    borderStyle: 'none',
    paddingInline: 0,
    alignItems: 'center',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-pending])': 'auto',
    },
    display: 'inline-flex',
    isolation: 'isolate',
    justifyContent: 'center',
    position: 'relative',
    transitionDuration: '0.2s',
    transitionProperty: 'color, background-color',
    transitionTimingFunction: 'easy-in-out',
    userSelect: 'none',
  },
});

const safeAreaStyles = stylex.create({ rounded: { borderRadius: 999 } });

const safeAreaVariants = stylex.create({
  primary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceTertiary,
    },
  },
  secondary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceAlternativeHover,
    },
  },
  tertiary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceAlternativeHover,
    },
  },
});

const sizeVariants = stylex.create({
  28: { gap: 6 },
  32: { gap: 8 },
  40: { gap: 6 },
  48: { gap: 8 },
  52: { gap: 10 },
  64: { gap: 12 },
});

const safeAreaSizeVariants = stylex.create({
  28: {
    borderRadius: 6,
    paddingInline: 12,
    height: 28,
  },
  32: {
    borderRadius: 8,
    paddingInline: 12,
    height: 32,
  },
  40: {
    borderRadius: 8,
    paddingInline: 16,
    height: 40,
  },
  48: {
    borderRadius: 10,
    paddingInline: 20,
    height: 48,
  },
  52: {
    borderRadius: 10,
    paddingInline: 28,
    height: 52,
  },
  64: {
    borderRadius: 14,
    paddingInline: 32,
    height: 64,
  },
});

const textStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'stroke, fill',
    transitionTimingFunction: 'easy-in-out',
  },
});

const TEXT_BUTTON_SLOTS = {
  LEFT_ICON: 'left-icon',
  RIGHT_ICON: 'right-icon',
};

export { TEXT_BUTTON_SLOTS, TextButton, TextButtonContext, type TextButtonProps };
